import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { GpsrResponse, missingMandatoryManufacturerFields } from 'app/model/gpsr.model';

/**
 * Mapowanie języka aplikacji na `lang` oczekiwany przez endpoint GPSR.
 * Endpoint honoruje parametr i odbija go w odpowiedzi; przy nieznanej wartości
 * odpowiada domyślnym `pl_PL`.
 */
const LANG_MAP: { [key: string]: string } = {
  pl: 'pl_PL',
  cz: 'cs_CZ',
  de: 'de_DE',
  sk: 'sk_SK',
};

const DEFAULT_LANG = 'pl_PL';

/**
 * Dane GPSR podmiotu odpowiedzialnego (issue #1710).
 *
 * Rozwiązanie TYMCZASOWE z założenia: docelowo dane mają przyjść z głównego backendu
 * razem z produktem. Dlatego cała wiedza o osobnym źródle siedzi w tym jednym serwisie
 * i w `environment.gpsrUrl` — przepięcie to zmiana tutaj, nie w komponentach.
 *
 * Świadomie na gołym `HttpClient`, a nie na `HttpService`: ten drugi wpina błędy
 * w globalną obsługę (m.in. overlay błędu technicznego), a nieosiągalne dane GPSR
 * nie mogą zepsuć całej karty produktu — brak danych to tu poprawny stan UI.
 */
@Injectable({
  providedIn: 'root',
})
export class GpsrService
{
  /**
   * Jedna odpowiedź na (kod rodziny, język) na czas życia karty. Endpoint deklaruje
   * `Cache-Control: public, max-age=300`, a przy zmianie wariantu w obrębie tej samej
   * rodziny dane GPSR się nie zmieniają — nie ma po co odpytywać ponownie.
   */
  private readonly cache = new Map<string, Observable<GpsrResponse | null>>();

  constructor(private http: HttpClient, private translate: TranslateService)
  {
  }

  public getByFamilyCode(familyCode: string): Observable<GpsrResponse | null>
  {
    if (!familyCode)
    {
      return of(null);
    }

    const lang = this.resolveLang();
    const cacheKey = familyCode + '|' + lang;
    const cached = this.cache.get(cacheKey);

    if (cached)
    {
      return cached;
    }

    const url = environment.gpsrUrl + 'v1/products/' + encodeURIComponent(familyCode) + '/gpsr?lang=' + lang;

    const request = this.http.get<GpsrResponse>(url).pipe(
      tap((response) => this.reportDataGaps(familyCode, response)),
      catchError((error) =>
      {
        // Brak danych GPSR nie może wywrócić karty produktu, ale nie chowamy tego po cichu:
        // najczęstsza przyczyna to zablokowany CORS (endpoint wpuszcza tylko wybrane originy),
        // a to jest problem konfiguracji, nie stan biznesowy.
        console.error('[gpsr] pobranie danych GPSR nieudane', { familyCode, lang, error });
        return of(null);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.cache.set(cacheKey, request);

    return request;
  }

  /**
   * Braki w danych wymaganych przez art. 19 GPSR zgłaszamy, zamiast je chować.
   * Puste pole i tak nie zostanie wyrenderowane, więc bez tego kroku brak danej
   * wymaganej prawem byłby całkowicie niewidoczny — a to jest dokładnie ten rodzaj
   * ciszy, którego przy treści regulowanej nie chcemy.
   */
  private reportDataGaps(familyCode: string, response: GpsrResponse | null): void
  {
    if (!response || response.available !== true)
    {
      return;
    }

    const missing = missingMandatoryManufacturerFields(response.manufacturer);

    if (missing.length)
    {
      console.warn('[gpsr] niekompletne dane producenta wymagane przez art. 19 GPSR', {
        familyCode,
        brakujacePola: missing,
      });
    }
  }

  private resolveLang(): string
  {
    const current = this.translate.currentLang || this.translate.defaultLang;

    return (current && LANG_MAP[current]) || DEFAULT_LANG;
  }
}
